import React, { useEffect, useMemo, useState } from 'react'
import intl from 'react-intl-universal'

import { SelectBox } from 'components/uikit-adapter/SelectBoxTyped'
import Button from 'components/button'
import StyledFilter from '../StyledFilter'
import { formatErrorMessage } from 'utils/handle-error'
import Fetch from 'utils/fetch'

import {
  searchStatusTypes,
  searchTiposUnidade,
  getCountries,
} from 'pages/master-data/unit-management/business-unit/business-unit-form/business-unit-form.service'

export type NullableText = string | null
export type NullableScalar = string | number | null

export type OptionItem = { Id: string | number; Description: string }
export type CountryItem = { Id: string | number; Descricao: string }
export type StatusItem = { Key: string | number; Value: string }

export type ExternalInitialData = {
  Nome?: NullableText
  SupplierId?: NullableScalar
  Destino?: NullableText
  TipoUnidadeEnumValue?: NullableScalar
  Pais?: NullableScalar
  StatusEnumValue?: NullableScalar
}

export interface BusinessUnitFilterProps {
  initialData?: ExternalInitialData
  handleClose: (params: Required<ExternalInitialData>) => void
}

const BusinessUnitFilter: React.FC<BusinessUnitFilterProps> = ({ initialData = {}, handleClose }) => {
  const [name, setName] = useState<NullableText>(null)
  const [supplierId, setSupplierId] = useState<NullableScalar>(null)
  const [destination, setDestination] = useState<NullableText>(null)
  const [unitTypeValue, setUnitTypeValue] = useState<NullableScalar>(null)
  const [countryId, setCountryId] = useState<NullableScalar>(null)
  const [statusValue, setStatusValue] = useState<NullableScalar>(null)

  const [statusTypes, setStatusTypes] = useState<StatusItem[]>([])
  const [unitTypes, setUnitTypes] = useState<OptionItem[]>([])
  const [countries, setCountries] = useState<CountryItem[]>([])
  const [suppliers, setSuppliers] = useState<OptionItem[]>([])
  const [names, setNames] = useState<OptionItem[]>([])
  const [destinations, setDestinations] = useState<OptionItem[]>([])

  const [isFetching, setIsFetching] = useState(false)

  const supplierSAPMessage = useMemo(() => intl.get('master-data.general-configuration.supplierSAP'), [])

  const filterBySelection = <T extends Record<string, any>>(
    list: T[] | undefined,
    selected: string | number | null | undefined,
    key: 'Id' | 'Key' = 'Id'
  ): T[] => {
    const safe = Array.isArray(list) ? list : []
    return selected == null ? safe : safe.filter((o) => String(o[key]) === String(selected))
  }

  const nameOptions = useMemo(() => filterBySelection(names, name, 'Id'), [names, name])
  const supplierOptions = useMemo(() => filterBySelection(suppliers, supplierId, 'Id'), [suppliers, supplierId])
  const destinationOptions = useMemo(() => filterBySelection(destinations, destination, 'Id'), [destinations, destination])
  const unitTypeOptions = useMemo(() => filterBySelection(unitTypes, unitTypeValue, 'Id'), [unitTypes, unitTypeValue])
  const countryOptions = useMemo(() => filterBySelection(countries as any, countryId, 'Id'), [countries, countryId])
  const statusOptions = useMemo(() => filterBySelection(statusTypes, statusValue, 'Key'), [statusTypes, statusValue])

  useEffect(() => {
    setName(initialData?.Nome ?? null)
    setSupplierId(initialData?.SupplierId ?? null)
    setDestination(initialData?.Destino ?? null)
    setUnitTypeValue(initialData?.TipoUnidadeEnumValue ?? null)
    setCountryId(initialData?.Pais ?? null)
    setStatusValue(initialData?.StatusEnumValue ?? null)

    const loadData = async () => {
      try {
        setIsFetching(true)

        const [status, unitTypesRes, countriesRes] = await Promise.all([
          searchStatusTypes() as Promise<StatusItem[]>,
          searchTiposUnidade() as Promise<OptionItem[]>,
          getCountries() as Promise<CountryItem[]>,
        ])

        const sapResp = await Fetch.get('business-units:sap-suppliers')
        const sapData: Array<string | number> = sapResp?.data ?? []
        const suppliersMapped: OptionItem[] = (sapData || []).map((sap) => ({ Id: sap, Description: String(sap) }))

        const namesResp = await Fetch.get('business-units:names')
        const namesData: string[] = namesResp?.data ?? []
        const namesMapped: OptionItem[] = (namesData || []).map((n) => ({ Id: n, Description: n }))

        const destResp = await Fetch.get('business-units:centers')
        const destData: string[] = destResp?.data ?? []
        const destMapped: OptionItem[] = (destData || []).map((d) => ({ Id: d, Description: d }))

        setStatusTypes(status || [])
        setUnitTypes(unitTypesRes || [])
        setCountries(countriesRes || [])
        setSuppliers(suppliersMapped)
        setNames(namesMapped)
        setDestinations(destMapped)
      } catch (e) {
        console.error(formatErrorMessage(e))
      } finally {
        setIsFetching(false)
      }
    }

    loadData()
  }, [])

  const confirmSearch = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault?.()
    handleClose({
      Nome: name ?? '',
      SupplierId: supplierId ?? null,
      Destino: destination ?? null,
      TipoUnidadeEnumValue: unitTypeValue ?? null,
      Pais: countryId ?? null,
      StatusEnumValue: statusValue ?? null,
    })
  }

  const clearFilters = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault?.()

    setName(null)
    setSupplierId(null)
    setDestination(null)
    setUnitTypeValue(null)
    setCountryId(null)
    setStatusValue(null)

    handleClose({
      Nome: null,
      SupplierId: null,
      Destino: null,
      TipoUnidadeEnumValue: null,
      Pais: null,
      StatusEnumValue: null,
    })
  }

  return (
    <StyledFilter>
      <div className="filter-container">
        <div className="sideFilterFields sideFilterFieldsHasFooter">
          <div>
            <SelectBox
              name="name"
              label={intl.get('master-data.general-configuration.name')}
              placeholder={intl.get('master-data.general-configuration.name')}
              valueKey="Id"
              labelKey="Description"
              value={name as any}
              options={nameOptions}
              onChange={(val: OptionItem | null) => setName(val ? String(val.Id) : null)}
              searchable
              openOnFocus
              clearable
              width="100%"
              isLoading={isFetching}
            />
          </div>

          <div>
            <SelectBox
              name="supplierId"
              label={supplierSAPMessage}
              placeholder={supplierSAPMessage}
              valueKey="Id"
              labelKey="Description"
              value={supplierId as any}
              options={supplierOptions}
              onChange={(val: OptionItem | null) => setSupplierId(val ? val.Id : null)}
              searchable
              openOnFocus
              clearable
              width="100%"
              isLoading={isFetching}
            />
          </div>

          <div>
            <SelectBox
              name="destination"
              label={intl.get('commons.destination') || 'Destination'}
              placeholder={intl.get('commons.destination') || 'Destination'}
              valueKey="Id"
              labelKey="Description"
              value={destination as any}
              options={destinationOptions}
              onChange={(val: OptionItem | null) => setDestination(val ? String(val.Id) : null)}
              searchable
              openOnFocus
              clearable
              width="100%"
              isLoading={isFetching}
            />
          </div>

          <div>
            <SelectBox
              name="unitTypeValue"
              label={intl.get('master-data.general-configuration.unityType')}
              placeholder={intl.get('master-data.general-configuration.unityType')}
              valueKey="Id"
              labelKey="Description"
              value={unitTypeValue as any}
              options={unitTypeOptions}
              onChange={(val: OptionItem | null) => setUnitTypeValue(val ? val.Id : null)}
              searchable
              openOnFocus
              clearable
              width="100%"
              isLoading={isFetching}
            />
          </div>

          <div>
            <SelectBox
              name="countryId"
              label={intl.get('commons.country')}
              placeholder={intl.get('commons.country')}
              valueKey="Id"
              labelKey="Descricao"
              value={countryId as any}
              options={countryOptions}
              onChange={(val: CountryItem | null) => setCountryId(val ? val.Id : null)}
              searchable
              openOnFocus
              clearable
              width="100%"
              isLoading={isFetching}
            />
          </div>

          <div>
            <SelectBox
              name="statusValue"
              label="Status"
              placeholder="Status"
              valueKey="Key"
              labelKey="Value"
              value={statusValue as any}
              options={statusOptions}
              onChange={(val: StatusItem | null) => setStatusValue(val ? val.Key : null)}
              searchable
              openOnFocus
              clearable
              width="100%"
              isLoading={isFetching}
            />
          </div>
        </div>

        <div className="footerFilterContainer no-margin">
          <div>
            <hr className="filter-divisor" />
          </div>

          <div className="footerFilterActions">
            <div className="footerBtn">
              <Button buttonType="primary" name="search-button" onClick={confirmSearch} value={intl.get('filters.filter')} />
            </div>

            <div className="footerBtn">
              <Button name="clean-search-button" onClick={clearFilters} value={intl.get('filters.cleanSearch')} />        
            </div>
          </div>
        </div>
      </div>
    </StyledFilter>
  )
}

export default BusinessUnitFilter
